import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Bell, Info, AlertTriangle, Wrench, Check, CheckCheck } from "lucide-react";
import { cn } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = { info: Info, warning: AlertTriangle, maintenance: Wrench };
const DOT_COLORS = {
  info: "bg-blue-400",
  warning: "bg-amber-400",
  maintenance: "bg-violet-400",
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default function NotificationBell() {
  const [announcements, setAnnouncements] = useState([]);
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("callintell_read_notifications") || "[]");
    } catch {
      return [];
    }
  });
  const ref = useRef(null);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const res = await axios.get(`${API}/announcements/active`);
        setAnnouncements(res.data || []);
      } catch {}
    };
    fetchAnnouncements();
    const interval = setInterval(fetchAnnouncements, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem("callintell_read_notifications", JSON.stringify(readIds));
  }, [readIds]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const unreadCount = announcements.filter((a) => !readIds.includes(a.id)).length;

  const markAsRead = (id) => {
    setReadIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const markAllRead = () => {
    setReadIds(announcements.map((a) => a.id));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className={cn(
          "relative h-8 w-8 flex items-center justify-center rounded-lg transition-colors",
          open
            ? "bg-slate-700/60 text-white"
            : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50"
        )}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white px-1 ring-2 ring-[#0B0D12] animate-in fade-in zoom-in duration-200"
          >
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-80 rounded-xl bg-[#11141D] border border-white/10 shadow-2xl shadow-black/40 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <h3 className="text-sm font-semibold text-slate-200">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                data-testid="mark-all-read-btn"
                className="flex items-center gap-1 text-[11px] text-blue-400 hover:text-blue-300 transition-colors"
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-72 overflow-y-auto">
            {announcements.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="h-8 w-8 text-slate-600 mx-auto mb-2" />
                <p className="text-xs text-slate-500">No notifications yet</p>
              </div>
            ) : (
              announcements.map((a) => {
                const Icon = ICONS[a.type] || Info;
                const dotColor = DOT_COLORS[a.type] || DOT_COLORS.info;
                const isRead = readIds.includes(a.id);
                return (
                  <div
                    key={a.id}
                    data-testid={`notification-item-${a.id}`}
                    onClick={() => markAsRead(a.id)}
                    className={cn(
                      "flex gap-3 px-4 py-3 cursor-pointer border-b border-white/[0.03] transition-colors",
                      isRead
                        ? "bg-transparent hover:bg-white/[0.02]"
                        : "bg-blue-500/[0.04] hover:bg-blue-500/[0.07]"
                    )}
                  >
                    <div className="relative flex-shrink-0 mt-0.5">
                      <div
                        className={cn(
                          "h-7 w-7 rounded-lg flex items-center justify-center",
                          a.type === "warning"
                            ? "bg-amber-500/10"
                            : a.type === "maintenance"
                            ? "bg-violet-500/10"
                            : "bg-blue-500/10"
                        )}
                      >
                        <Icon
                          className={cn(
                            "h-3.5 w-3.5",
                            a.type === "warning"
                              ? "text-amber-400"
                              : a.type === "maintenance"
                              ? "text-violet-400"
                              : "text-blue-400"
                          )}
                        />
                      </div>
                      {!isRead && (
                        <span
                          className={cn(
                            "absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-[#11141D]",
                            dotColor
                          )}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          "text-xs leading-snug",
                          isRead ? "text-slate-400" : "text-slate-200 font-medium"
                        )}
                      >
                        {a.title}
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">
                        {a.message}
                      </p>
                      <p className="text-[10px] text-slate-600 mt-1">{timeAgo(a.created_at)}</p>
                    </div>
                    {isRead && <Check className="h-3 w-3 text-slate-600 flex-shrink-0 mt-1" />}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
